/**
 * i18n Formatting Utilities
 * Locale-aware price and date formatting.
 * Reads the user's saved language preference, falling back to REACT_APP_LOCALE.
 */

import { getLanguagePreference } from '@/i18n';
import i18n from '@/i18n';

const LOCALE_CURRENCY_MAP: Record<string, string> = {
  'en-GB': 'GBP',
  'en-US': 'USD',
  'it': 'EUR',
  'es': 'EUR',
};

/**
 * Get the current locale from user preference or environment variable.
 */
export function getLocale(): string {
  return getLanguagePreference();
}

/**
 * Get the currency code for the current locale.
 */
export function getCurrency(): string {
  return LOCALE_CURRENCY_MAP[getLocale()] || 'GBP';
}

/**
 * Format a price amount using Intl.NumberFormat.
 */
export function formatPrice(
  amount: number,
  options?: { locale?: string; currency?: string; minimumFractionDigits?: number }
): string {
  const locale = options?.locale || getLocale();
  const currency = options?.currency || getCurrency();
  const minimumFractionDigits = options?.minimumFractionDigits ?? (amount === 0 ? 0 : 2);

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format a price for display in pricing cards (no decimals for round numbers).
 */
export function formatPriceCompact(amount: number): string {
  return formatPrice(amount, { minimumFractionDigits: 0 });
}

/**
 * Format a date string using the configured locale.
 */
export function formatDate(
  dateString: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const locale = getLocale();
  const date = new Date(dateString);
  const defaultOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  return date.toLocaleDateString(locale, options || defaultOptions);
}

/**
 * Format a date with relative time for recent dates, absolute for older dates.
 */
export function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return i18n.t('time.justNow');
  if (diffMins < 60) return i18n.t('time.minutesAgo', { count: diffMins });
  if (diffHours < 24) return i18n.t('time.hoursAgo', { count: diffHours });
  if (diffDays < 7) return i18n.t('time.daysAgo', { count: diffDays });

  return formatDate(dateString);
}
